import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

import { getParameter } from '@hotjar/insights-common/lib/url';
import { CONTROLS_AND_ELEMENTS_IDS } from '@hotjar/recordings/src/constants/heatmaps-retaker';
import {
  readOnly,
  setAutoplay,
  pause,
  performTrackRecordingViewed,
  jumpTo,
  play,
  togglePlaybackSpeed,
  setSkipPauses,
  rewind,
  restart,
  forward,
  hasPreviousRecording,
  previousRecording,
  hasNextRecording,
  nextRecording,
  toggleSidePanel,
  toggleJumpStart,
  toggleMuteClicks,
  hideForcePlaybackTooltip,
  toggleAutoplay,
  toggleSkipPauses,
  togglePlayPause,
} from '@hotjar/recordings/src/features/Playbacks/utils';
import { useSelector, useStore, useStoreState } from '@hotjar/recordings/src/store/player/context';
import { Console } from '@hotjar/recordings/src/features/RecordingsPage/components/Console';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { useErrorReportingSuggestion } from '@hotjar/webapp/features/Playbacks/SessionInfo/hook/useErrorReportingSuggestion';
import { TileIds as TILES } from '@hotjar/webapp/domains/activation/start-page/types';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { Site } from '@hotjar/site';

import { showContent as showAppcuesContent } from '../../../../tracking/tools/appcues';
import { useTimeline } from '../../Timeline';
import { useModalStore } from '../../store/modalStore';
import { useBoundingBox } from '../../../../hooks/useBoundingBox';
import { getIsRetakerMode } from '../../store/utils';
import { useRecordingHighlightCreate } from '../../../Highlights/Recordings/use-recording-highlight-create';
import { HIGHLIGHTS_ONBOARDING_ID } from '../../../../constants/appcues';
import { useNotify } from '../../../../hooks/useNotify';
import { trackClick } from '../../utils/tracking';
import { CommentsContext } from '../../../../contexts/comments';
import { HighlightsTimeline as Highlights } from '../../../Highlights/Recordings';
import { TimelineList } from '../../TimelineList';
import { useRealTimeStore } from '../../store/realTimeStore';
import { Controls, RelevantActionNotification, Timeline } from '../..';
import { CONTROL_Z_INDEX } from '../../constants';
import { TIMELINE_PADDING } from '../../Timeline/constants';
import { KeyboardShortcutEvents } from '../../Controls/KeyboardShortcutEvents';
import { ShortcutsLegendModal } from '../../Controls/Modals/ShortcutsLegendModal';
import { DeleteRecordingModal } from '../../Controls/Modals/DeleteRecordingModal';
import { ShareModal } from '../../ShareModal';

import { ItemType, ModalStateEnum } from '../../types';
import type {
  Recording,
  TimelineNewItemMetadata,
  JumpDirectionType,
  TimelineActiveItem,
} from '../../types';

const WrapperDiv = styled.div`
  background-color: ${tokens.BRAND_COLORS.white};
  border-top: 1px solid ${tokens.color.neutral_300};

  width: 100%;
  bottom: 0;
  // z-index needed to be on top of the preview element
  z-index: ${CONTROL_Z_INDEX};
  left: 0;
`;

const TimelineRelatedWrapperDiv = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: ${tokens.spacer.x1};
`;

const RelevantActionNotificationContainer = styled.div`
  position: absolute;
  bottom: 16px;
  left: ${TIMELINE_PADDING}px;
  width: calc(100% - ${TIMELINE_PADDING * 2}px);
`;

const ContentContainer = styled.div`
  display: block;
  height: 11px;
  margin: 0;
  bottom: 33px;
  position: absolute;
  left: ${TIMELINE_PADDING}px;
  width: calc(100% - ${TIMELINE_PADDING * 2}px);
`;

const trackJumpTo = (
  recording: Recording,
  jumpDirection: JumpDirectionType,
  uiPosition: string,
) => {
  const trackingParams = {
    element_name: `playback_jumped_${jumpDirection}`,
    state_event_was_triggered_on: 'playback.player_navigation',
    ui_position: uiPosition,
  };

  trackClick(recording, trackingParams);
};

export const BottomSection = (): JSX.Element => {
  const playerStore = useStore();
  const { setShared } = useStoreState();

  const {
    playing,
    loaded,
    recording,
    sidePanelEventMarkers,
    done,
    manuallyPausedOnce,
    isAutoplaying,
    isSharedRecording,
    user,
    sharingToken,
    organizationId,
    siteId,
    eventMarkers,
    playbackSpeed,
    skipPauses,
    isJumpStartActive,
    isMuteClicksActive,
    isForcingPlayback,
    isConsoleOpen,
    isShared,
    site,
  } = useSelector((state) => ({
    playing: state.playing,
    loaded: state.loaded,
    recording: state.recording,
    sidePanelEventMarkers: state.sidePanelEventMarkers,
    done: state.done,
    manuallyPausedOnce: state.manuallyPausedOnce,
    isAutoplaying: state.isAutoplaying,
    isSharedRecording: state.isSharedRecording,
    user: state.user,
    sharingToken: state.sharingToken,
    organizationId: state.organizationId,
    siteId: state.siteId,
    eventMarkers: state.eventMarkers,
    playbackSpeed: state.playbackSpeed,
    skipPauses: state.skipPauses,
    isJumpStartActive: state.isJumpStartActive,
    isMuteClicksActive: state.isMuteClicksActive,
    isForcingPlayback: state.isForcingPlayback,
    isConsoleOpen: state.isConsoleOpen,
    isShared: state.isShared,
    site: state.site,
  }));

  const { success } = useNotify();
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();

  const activeModal = useModalStore((state) => state.activeModal);

  const shouldTrack = !getIsRetakerMode();
  const [activeItem, setActiveItem] = useState<TimelineActiveItem | null>(null);
  const resetActiveItem = useCallback(() => setActiveItem(null), [setActiveItem]);

  const { setActiveModal } = useModalStore.getState();
  const [shareModalUIPosition, setShareModalUIPosition] = useState<string | undefined>();
  const [currentNewItemMetadata, setCurrentNewItemMetadata] = useState<TimelineNewItemMetadata>({
    type: ItemType.HIGHLIGHT,
    time: null,
  });

  const [timelineBox, setTimelineBox] = useBoundingBox();

  const setItemMetadataTime = useCallback(
    (time: number | null) => {
      if (currentNewItemMetadata.time !== time) {
        setCurrentNewItemMetadata((prevState) => ({ ...prevState, time }));
      }
    },
    [setCurrentNewItemMetadata, currentNewItemMetadata],
  );

  const jumpToWrapper = useCallback(
    (targetTime, uiPosition) => {
      const jumpDirection: JumpDirectionType =
        useRealTimeStore.getState().currentTime < targetTime ? 'forward' : 'backward';
      if (shouldTrack) {
        trackJumpTo(recording as Recording, jumpDirection, uiPosition);
      }
      jumpTo(playerStore, targetTime);
    },
    [recording, shouldTrack, playerStore],
  );

  const { suggestion: errorReportingSuggestion, dismiss: dismissErrorCaptureTile } =
    useErrorReportingSuggestion(site as Site);

  const onEnableCapture = useCallback(() => {
    queryClient.invalidateQueries('session-settings');

    // Covering the case where capturing is enabled/disabled after the fist time.
    if (!errorReportingSuggestion) {
      return;
    }

    const trackingData = {
      element_name: `${TILES.ERROR_REPORTING} Dismiss - Console enabled capture`,
      state_event_was_triggered_on: window.location.href,
      ui_position: 'Suggestion Tile - Player',
    };

    trackClick(recording as Recording, trackingData);
    dismissErrorCaptureTile();
  }, [errorReportingSuggestion, dismissErrorCaptureTile, trackClick, recording]);

  const isReadOnly = readOnly(playerStore);
  const playWrapper = (showAnimation?: boolean) => play(playerStore, showAnimation);
  const onAutoplayToggle = (checked: boolean) => setAutoplay(playerStore, checked);
  const pauseWrapper = (showAnimation?: boolean, isManual?: boolean) => {
    pause(playerStore, showAnimation, isManual);
  };

  const { showNewItemNow } = useTimeline({
    itemTime: null,
    isAutoplaying,
    isDone: done,
    onAutoplayToggle,
    pause: pauseWrapper,
    setItemMetadataTime,
    setCurrentNewItemMetadata,
  });

  const isRelevantActionOverlappingHighlight = useRealTimeStore(
    (state) => state.isRelevantActionOverlappingHighlight,
  );

  const skipCurrentInactivityWrapper = useCallback(() => {
    const realTimeStoreState = useRealTimeStore.getState();
    const currentSection =
      realTimeStoreState.sectionInfoList[realTimeStoreState.currentSectionIndex];
    if (currentSection && !currentSection.isActive) {
      const targetTime = currentSection.startTimeInMs + currentSection.durationInMs + 1;
      // Warning: We should always call jumpTo to change the currentTime.
      // We can afford to do that now because we are sure we won't change page in that jump
      realTimeStoreState.setCurrentTime(Math.min(targetTime, realTimeStoreState.duration));
    }
  }, []);

  const { mutateAsync: createRecordingHighlight } = useRecordingHighlightCreate(String(siteId));
  const onAddLabel = async (labelId: string) => {
    await createRecordingHighlight({
      highlightTime: useRealTimeStore.getState().currentTime,
      recordingId: recording?.id || 0,
      labels: [labelId],
      uiPosition: 'sticker label',
    });

    success(t('common:player.toast-saved-highlight'));
  };

  useEffect(() => {
    const shouldTriggerHighlightsAppcuesFlow =
      getParameter(window.location.href, 'highlights-flow') === 'true';

    if (shouldTriggerHighlightsAppcuesFlow) {
      showAppcuesContent(HIGHLIGHTS_ONBOARDING_ID);
    }
  }, []);

  const isShownRelevantActionNotification = !playing && !done && manuallyPausedOnce;
  const isNewHighlightsOn = !isSharedRecording;
  const userEmail = user?.user_email;
  const userPictureUrl = user?.user_picture_url;
  const userName = user?.user_name;
  const userId = user?.user_id;

  return (
    <footer className="bottom-section">
      <Console onEnableCapture={onEnableCapture} />

      <WrapperDiv>
        <TimelineRelatedWrapperDiv>
          {loaded &&
          recording &&
          isShownRelevantActionNotification &&
          !readOnly(playerStore) &&
          !currentNewItemMetadata?.time &&
          !isRelevantActionOverlappingHighlight ? (
            <RelevantActionNotificationContainer
              id={CONTROLS_AND_ELEMENTS_IDS.RELEVANT_ACTIONS_CONTAINER}
            >
              <RelevantActionNotification
                onShare={() => {
                  setShareModalUIPosition('clicked_share_in_actions_onpause_popover');
                  setActiveModal(ModalStateEnum.SHARE_RECORDING_OPEN);
                }}
                onCreate={showNewItemNow}
                recording={recording}
                showStickerLabels={isNewHighlightsOn}
              />
            </RelevantActionNotificationContainer>
          ) : null}

          {loaded && recording && userId && isNewHighlightsOn ? (
            <CommentsContext>
              <ContentContainer id={CONTROLS_AND_ELEMENTS_IDS.COMMENTS_CONTAINER}>
                <Highlights
                  apiToken={sharingToken}
                  currentUser={{
                    id: userId,
                    name: userName as string,
                    avatar: userPictureUrl as string,
                    email: userEmail as string,
                  }}
                  activeItemId={activeItem?.id || null}
                  duration={recording?.duration || 0}
                  isPlaying={playing}
                  newHighlightMetadata={currentNewItemMetadata}
                  onCommentsFetched={() => performTrackRecordingViewed(playerStore)}
                  onNewHighlightTimeChange={setItemMetadataTime}
                  onPause={pauseWrapper}
                  onShare={() => {
                    setShareModalUIPosition('clicked_share_in_comment');
                    setActiveModal(ModalStateEnum.SHARE_RECORDING_OPEN);
                  }}
                  organizationId={organizationId as number}
                  recordingId={recording?.id}
                  resetActiveItem={resetActiveItem}
                  setActiveItem={setActiveItem}
                  timelineBox={timelineBox}
                  jumpTo={(targetTime: number) => jumpTo(playerStore, targetTime)}
                  onPlay={playWrapper}
                  pause={pauseWrapper}
                  loaded={loaded}
                />
                <TimelineList
                  duration={recording?.duration}
                  activeItemId={activeItem?.id ?? -1}
                  recordingId={recording?.id}
                  siteId={siteId}
                />
              </ContentContainer>
            </CommentsContext>
          ) : null}
          {loaded && recording ? (
            <Timeline
              duration={recording?.duration}
              jumpTo={jumpToWrapper}
              eventMarkers={eventMarkers}
              sidePanelEventMarkers={sidePanelEventMarkers}
              isDesktopRecording={recording.device === 'desktop'}
              siteId={recording.site_id}
              isPlaying={playing}
              setTimelineBox={setTimelineBox}
            />
          ) : null}
        </TimelineRelatedWrapperDiv>
        <Controls
          shouldTrack={shouldTrack}
          isTokenShared={isSharedRecording}
          currentSpeed={playbackSpeed}
          onSpeedChange={() => togglePlaybackSpeed(playerStore)}
          isSkippingAllPauses={skipPauses}
          onSkipPausesToggle={(checked) => setSkipPauses(playerStore, checked)}
          duration={recording?.duration}
          onRewind={(milliseconds) => rewind(playerStore, milliseconds)}
          onPlay={playWrapper}
          onReplay={() => restart(playerStore)}
          onPause={pauseWrapper}
          onSkipCurrentInactivity={skipCurrentInactivityWrapper}
          onNewHighlight={showNewItemNow}
          onAddLabel={onAddLabel}
          isPlaying={playing}
          isDone={done}
          onForward={(milliseconds) => forward(playerStore, milliseconds)}
          onAutoplayToggle={onAutoplayToggle}
          isAutoplaying={isAutoplaying}
          isJumpStartActive={isJumpStartActive}
          onToggleJumpStart={() => toggleJumpStart(playerStore)}
          isMuteClicksActive={isMuteClicksActive}
          onToggleMuteClicks={() => toggleMuteClicks(playerStore)}
          loaded={loaded}
          recording={recording as Recording}
          isForcingPlayback={isForcingPlayback}
          onForcePlaybackClose={() => hideForcePlaybackTooltip(playerStore)}
          showStickerLabels={isNewHighlightsOn}
          activeModal={activeModal}
          setActiveModal={setActiveModal}
          isConsoleOpen={isConsoleOpen}
        />
      </WrapperDiv>

      {/** @TODO Extract and move to standalone file when possible */}
      <KeyboardShortcutEvents
        recording={recording as Recording}
        hasNextRecording={hasNextRecording(playerStore)}
        hasPreviousRecording={hasPreviousRecording(playerStore)}
        isNewHighlightsOn={isNewHighlightsOn}
        isReadOnly={isReadOnly}
        isTokenShared={isSharedRecording}
        jumpTo={(targetTime: number) => jumpTo(playerStore, targetTime)}
        isSkippingAllPauses={skipPauses}
        skipPauses={skipPauses}
        onSkipCurrentInactivity={skipCurrentInactivityWrapper}
        onPlayPauseToggle={(event) => togglePlayPause(playerStore, event, helpHotjarUrl)}
        onPlaybackSpeedToggle={() => togglePlaybackSpeed(playerStore)}
        onExpandCollapseToggle={() => toggleSidePanel(playerStore)}
        onSkipPausesToggle={() => toggleSkipPauses(playerStore)}
        onReplay={() => restart(playerStore)}
        onForward={(milliseconds) => forward(playerStore, milliseconds)}
        onRewind={(milliseconds) => rewind(playerStore, milliseconds)}
        onNext={() => nextRecording(playerStore)}
        onPrevious={() => previousRecording(playerStore)}
        onNewComment={showNewItemNow}
        onDelete={() => setActiveModal(ModalStateEnum.DELETE_RECORDING_OPEN)}
        onAutoplayToggle={() => {
          toggleAutoplay(playerStore);
          success(isAutoplaying ? 'Autoplay disabled' : 'Autoplay enabled');
        }}
        onShortcutsModalToggle={() =>
          activeModal === ModalStateEnum.KEYBOARD_SHORTCUT_OPEN
            ? setActiveModal(ModalStateEnum.CLOSE)
            : setActiveModal(ModalStateEnum.KEYBOARD_SHORTCUT_OPEN)
        }
      />

      <ShortcutsLegendModal
        isOpen={activeModal === ModalStateEnum.KEYBOARD_SHORTCUT_OPEN}
        onClose={() => setActiveModal(ModalStateEnum.CLOSE)}
        isNewHighlightsOn={isNewHighlightsOn}
        isReadOnly={isReadOnly}
        isTokenShared={isSharedRecording}
        isPlaying={playing}
        onPause={playWrapper}
        onPlay={pauseWrapper}
      />
      {recording ? (
        <DeleteRecordingModal
          recording={recording as Recording}
          onPlay={playWrapper}
          onPause={pauseWrapper}
          isPlaying={playing}
        />
      ) : null}

      {loaded && recording ? (
        <ShareModal
          isPlaying={playing}
          isShared={isShared}
          play={playWrapper}
          pause={pauseWrapper}
          isOpen={activeModal === ModalStateEnum.SHARE_RECORDING_OPEN}
          recording={recording}
          onClose={() => setActiveModal(ModalStateEnum.CLOSE)}
          onShare={setShared}
          uiPosition={shareModalUIPosition as string}
        />
      ) : null}
    </footer>
  );
};
